import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'store/auth/models';
import {
  Entity,
  Organisation,
  ReceiptEntityStatistics,
} from 'store/organisation/models';

/**
 * Latest column renderers. Assigned on each EntityComponent render so a
 * memoised cell can call the current closure without rebuilding columns.
 * Only one entities table is mounted.
 */
export const entityCellApi = {
  renderOrgLink: (
    _content: string,
    _record: Entity
  ): React.ReactNode => null,
  renderTaxReturn: (
    _content: string,
    _record: Entity
  ): React.ReactNode => null,
  renderCaptureStats: (_record: Entity): React.ReactNode => null,
  renderUsers: (_record: Entity): React.ReactNode => null,
  renderVat: (_content: string): React.ReactNode => null,
  renderCountry: (_record: Entity): React.ReactNode => null,
  renderConnectionStatus: (_record: Entity): React.ReactNode => null,
  renderEntityActions: (_record: Entity): React.ReactNode => null,
};

export type EntityTableContextValue = {
  currentUser?: User;
  hasAdvancedUserRights: boolean;
  members?: User[];
  membersLoading: boolean;
};

/** User, licence, and member data shared by return and avatar cells. */
export const EntityTableContext =
  React.createContext<EntityTableContextValue | null>(null);

/** Pin icon. Separate so a menu click does not redraw name cells. */
export const EntityPinContext = React.createContext<{
  pinnedEntityIds: string[];
} | null>(null);

/** Which row menu is open. Only the actions cell subscribes. */
export const EntityMenuContext = React.createContext<{
  openContextMenuId: string | null;
  showThreeDotsMarker: boolean;
} | null>(null);

/** Capture stats live outside the entity record. */
export const EntityCaptureContext = React.createContext<{
  statsByConnectionId: Map<string, ReceiptEntityStatistics>;
  organisations?: Organisation[];
} | null>(null);

export const EntityOrgCell = ({
  content,
  record,
}: {
  content: string;
  record: Entity;
}) => {
  useTranslation();
  useContext(EntityPinContext);
  return entityCellApi.renderOrgLink(content, record);
};

export const EntityReturnsCell = ({
  content,
  record,
}: {
  content: string;
  record: Entity;
}) => {
  useTranslation();
  useContext(EntityTableContext);
  return entityCellApi.renderTaxReturn(content, record);
};

export const EntityCaptureCell = ({ record }: { record: Entity }) => {
  useTranslation();
  useContext(EntityCaptureContext);
  return entityCellApi.renderCaptureStats(record);
};

export const EntityUsersCell = ({ record }: { record: Entity }) => {
  useTranslation();
  useContext(EntityTableContext);
  return entityCellApi.renderUsers(record);
};

export const EntityVatCell = ({ content }: { content?: string | null }) => {
  return entityCellApi.renderVat(content ?? '');
};

export const EntityCountryCell = ({ record }: { record: Entity }) => {
  useTranslation();
  return entityCellApi.renderCountry(record);
};

export const EntityConnectionCell = ({ record }: { record: Entity }) => {
  useTranslation();
  return entityCellApi.renderConnectionStatus(record);
};

export const EntityActionsCell = ({ record }: { record: Entity }) => {
  useTranslation();
  useContext(EntityTableContext);
  useContext(EntityMenuContext);
  return entityCellApi.renderEntityActions(record);
};
